/**
 * Founder OS — native product screens.
 * Each export is a fully-built React widget that mimics a real Founder OS screen:
 * sidebar+content dashboards, chat, kanban, community, mentor, investor,
 * marketplace, founder-score, mobile mini-apps. All Tailwind, no images.
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Home, Rocket, Sparkles, Users, MessageSquare, Calendar, BookOpen, Trophy,
  TrendingUp, Handshake, Bell, Search, LogOut, Check, ChevronRight, Circle,
  Zap, Send, Paperclip, Play, DollarSign, Building2, Award, BarChart3,
  Star, Clock, MapPin, ArrowUpRight, ArrowRight, Flame, MoreHorizontal,
  Briefcase, GraduationCap, PenLine, Target, Compass,
} from "lucide-react";
import { FounderOSMark } from "./FounderOSMark";

/* ------------------------------------------------------------------ */
/* Shared UI primitives                                               */
/* ------------------------------------------------------------------ */

/** Browser-style chrome wrapper */
export const ScreenFrame = ({ children, title = "app.bowerschool.com", className = "" }) => (
  <div className={`rounded-xl md:rounded-2xl overflow-hidden border border-white/10 shadow-[0_40px_120px_-30px_rgba(66,66,255,0.5)] bg-[#0b0b12] ${className}`}>
    <div className="flex items-center gap-2 px-4 py-3 border-b border-white/[0.06] bg-black/60">
      <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]/70" />
      <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]/70" />
      <span className="ml-3 text-[11px] text-white/40 font-grotesk truncate">{title}</span>
    </div>
    <div className="bg-[#0b0b12]">{children}</div>
  </div>
);

/** Sidebar shared across most dashboard screens */
const NAV = [
  { icon: Home, label: "Dashboard", active: true },
  { icon: Rocket, label: "Venture" },
  { icon: Sparkles, label: "AI Copilot", badge: "AI" },
  { icon: Users, label: "Mentors" },
  { icon: MessageSquare, label: "Community" },
  { icon: BookOpen, label: "Learning" },
  { icon: Calendar, label: "Events" },
  { icon: PenLine, label: "Journal" },
  { icon: Trophy, label: "Founder Score" },
];
const NAV_GROWTH = [
  { icon: TrendingUp, label: "Investor Connect" },
  { icon: Handshake, label: "Cofounder Match" },
];

export const Sidebar = ({ activeLabel = "Dashboard" }) => (
  <aside className="w-[220px] flex-shrink-0 bg-black/40 border-r border-white/[0.06] py-5 px-3 hidden md:flex flex-col gap-6">
    <div className="flex items-center gap-2 px-2">
      <div className="relative w-6 h-6 rounded-md bg-gradient-to-br from-[#4242FF] to-[#7C3AED] flex items-center justify-center">
        <div className="w-2 h-2 rounded-full bg-white/90" />
      </div>
      <div className="flex items-center gap-1.5">
        <span className="text-white text-sm font-semibold tracking-tight">Bower</span>
        <span className="text-[9px] uppercase tracking-widest text-white/40">Founder OS</span>
      </div>
    </div>
    <nav className="flex flex-col gap-0.5">
      {NAV.map((item) => {
        const active = item.label === activeLabel;
        return (
          <div
            key={item.label}
            className={`flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-grotesk transition-colors ${active ? "bg-white/[0.08] text-white" : "text-white/60 hover:text-white/85"}`}
          >
            <item.icon size={14} className={active ? "text-white" : "text-white/50"} />
            <span className="flex-1">{item.label}</span>
            {item.badge && (
              <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-gradient-to-r from-[#FF6B6B] to-[#F06292] text-white">
                {item.badge}
              </span>
            )}
          </div>
        );
      })}
    </nav>
    <div>
      <div className="px-2.5 pb-2 text-[9px] uppercase tracking-widest text-white/30">Growth</div>
      <nav className="flex flex-col gap-0.5">
        {NAV_GROWTH.map((item) => (
          <div key={item.label} className="flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] text-white/60 hover:text-white/85 font-grotesk">
            <item.icon size={14} className="text-white/50" />
            <span>{item.label}</span>
          </div>
        ))}
      </nav>
    </div>
  </aside>
);

/** Top bar */
export const TopBar = ({ live = true }) => (
  <div className="flex items-center gap-3 px-5 py-3 border-b border-white/[0.06]">
    <div className="flex items-center flex-1 gap-2 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 max-w-md">
      <Search size={13} className="text-white/40" />
      <span className="text-[12px] text-white/40 font-grotesk">Search anything…</span>
      <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-white/[0.06] text-white/50">⌘K</span>
    </div>
    {live && (
      <div className="flex items-center gap-1.5 px-2 py-1 rounded-full bg-[#FF6B6B]/15 border border-[#FF6B6B]/25">
        <span className="w-1.5 h-1.5 rounded-full bg-[#FF6B6B] animate-pulse" />
        <span className="text-[10px] font-grotesk font-semibold text-[#FFB4B4] uppercase tracking-widest">Live · B-Cafe</span>
      </div>
    )}
    <div className="flex items-center gap-2">
      <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center relative">
        <Bell size={13} className="text-white/60" />
        <span className="absolute -top-1 -right-1 min-w-[14px] h-[14px] px-1 rounded-full bg-[#FF6B6B] text-[9px] font-bold text-white flex items-center justify-center">6</span>
      </div>
      <div className="w-7 h-7 rounded-lg bg-white/[0.04] border border-white/10 flex items-center justify-center">
        <LogOut size={13} className="text-white/60" />
      </div>
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* 1. Dashboard screen (Hero + Meet)                                  */
/* ------------------------------------------------------------------ */

const KPI = ({ icon: Icon, label, value, delta, dark = false }) => (
  <div className={`rounded-xl px-4 py-3 border ${dark ? "bg-white/[0.08] border-white/15" : "bg-white/[0.02] border-white/[0.06]"} flex-1 min-w-0`}>
    <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-white/40 font-grotesk mb-1.5">
      <Icon size={11} />
      <span className="truncate">{label}</span>
    </div>
    <div className="flex items-baseline gap-2">
      <span className={`text-lg font-semibold tracking-tight ${dark ? "text-white" : "text-white/95"}`}>{value}</span>
      {delta && <span className="text-[10px] text-emerald-400 font-grotesk">{delta}</span>}
    </div>
  </div>
);

export const DashboardScreen = () => (
  <ScreenFrame>
    <div className="flex min-h-[540px]">
      <Sidebar activeLabel="Dashboard" />
      <div className="flex-1 min-w-0">
        <TopBar />
        <div className="p-5 md:p-6">
          {/* Greeting card */}
          <div className="relative overflow-hidden rounded-xl border border-white/[0.06] p-5 md:p-6 mb-4" style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.15) 0%, rgba(66,66,255,0.08) 100%)" }}>
            <div className="text-[10px] uppercase tracking-widest text-white/50 font-grotesk mb-1.5">Wednesday, 22 July · Year 2 · Operationalize</div>
            <h3 className="font-display text-white text-2xl md:text-3xl font-semibold tracking-tight leading-tight" style={{ fontVariationSettings: "'opsz' 32" }}>
              Good morning, <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #4242FF, #A855F7)" }}>Rahul.</span>
            </h3>
            <p className="text-[13px] text-white/60 mt-2 max-w-md">You have <span className="text-white font-medium">5 things</span> on the calendar, 1 MVP submission tonight, and a Super Mentor 1:1. Let&rsquo;s build.</p>
            <button className="mt-4 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-gradient-to-r from-[#FF9F43] to-[#FF6B6B] text-white text-[12px] font-semibold font-grotesk shadow-lg">
              <Sparkles size={12} /> Plan my day with Copilot
            </button>
            <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full opacity-40 blur-2xl" style={{ background: "radial-gradient(circle, #8B5CF6, transparent 70%)" }} />
          </div>
          {/* KPI row */}
          <div className="flex gap-3 mb-4">
            <KPI icon={Trophy} label="Founder Score" value="842" delta="+24" dark />
            <KPI icon={Rocket} label="Venture Stage" value="MVP" delta="48%" />
            <KPI icon={Users} label="Customers" value="12" delta="+3 wk" />
            <KPI icon={Handshake} label="Mentor Hrs" value="14.5" delta="+2.5" />
          </div>
          {/* Today + activity */}
          <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-3">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] uppercase tracking-widest text-white/50 font-grotesk">Today · Wed 22</span>
                <span className="text-[11px] text-white/40 font-grotesk">3 events</span>
              </div>
              <div className="flex flex-col gap-2">
                {[
                  { time: "10:30", title: "Super Mentor 1:1 — Ajit R.", tag: "Mentor", color: "#8B5CF6" },
                  { time: "13:00", title: "Growth sprint — Cohort C", tag: "Cohort", color: "#4242FF" },
                  { time: "18:00", title: "MVP Submission — Founder OS", tag: "Deliverable", color: "#F06292", pulse: true },
                ].map((row, i) => (
                  <div key={i} className="flex items-center gap-3 px-3 py-2.5 rounded-lg bg-white/[0.03] border border-white/[0.05]">
                    <span className="text-[11px] font-grotesk text-white/50 w-10">{row.time}</span>
                    <div className="w-1 h-8 rounded-full" style={{ background: row.color }} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] text-white truncate">{row.title}</div>
                      <div className="flex items-center gap-1 mt-0.5">
                        <span className="text-[9px] uppercase tracking-widest font-grotesk" style={{ color: row.color }}>{row.tag}</span>
                        {row.pulse && <span className="w-1 h-1 rounded-full bg-[#F06292] animate-pulse ml-1" />}
                      </div>
                    </div>
                    <ChevronRight size={12} className="text-white/30" />
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] uppercase tracking-widest text-white/50 font-grotesk">Founder Score · trend</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 font-grotesk">↑ 12%</span>
              </div>
              <div className="text-[38px] leading-none font-display font-semibold text-white tracking-tight mb-3" style={{ fontVariationSettings: "'opsz' 48" }}>842</div>
              <MiniSparkline values={[420, 450, 490, 520, 560, 610, 680, 720, 760, 790, 820, 842]} />
              <div className="flex justify-between text-[10px] text-white/40 font-grotesk mt-2"><span>Aug</span><span>Now</span></div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ScreenFrame>
);

/** Inline SVG sparkline */
const MiniSparkline = ({ values, color = "#A855F7" }) => {
  const w = 240, h = 60;
  const max = Math.max(...values), min = Math.min(...values);
  const pts = values.map((v, i) => {
    const x = (i / (values.length - 1)) * w;
    const y = h - ((v - min) / (max - min || 1)) * h;
    return `${x},${y}`;
  }).join(" ");
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-14">
      <defs>
        <linearGradient id="spark-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.35" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <polygon points={`0,${h} ${pts} ${w},${h}`} fill="url(#spark-fill)" />
    </svg>
  );
};

/* ------------------------------------------------------------------ */
/* 2. Venture / Build screen                                          */
/* ------------------------------------------------------------------ */

export const VentureScreen = () => {
  const cols = [
    { title: "Idea", accent: "#8B5CF6", items: [
      { t: "Interview 5 target users", tag: "Discovery" },
      { t: "Value-prop draft", tag: "Positioning" },
    ]},
    { title: "MVP", accent: "#4242FF", items: [
      { t: "Auth + onboarding", tag: "Build" },
      { t: "Kanban module", tag: "Build", check: true },
      { t: "Landing page v1", tag: "Marketing" },
    ]},
    { title: "Traction", accent: "#06B6D4", items: [
      { t: "First 10 paid users", tag: "Growth" },
      { t: "Referral loop", tag: "Growth" },
    ]},
    { title: "Scale", accent: "#10B981", items: [
      { t: "Series A deck", tag: "Raise", check: true },
    ]},
  ];
  return (
    <ScreenFrame title="app.bowerschool.com — /venture">
      <div className="flex min-h-[520px]">
        <Sidebar activeLabel="Venture" />
        <div className="flex-1 min-w-0">
          <TopBar />
          <div className="p-5 md:p-6">
            <div className="flex items-center justify-between mb-5">
              <div>
                <h3 className="font-display text-white text-xl md:text-2xl font-semibold tracking-tight" style={{ fontVariationSettings: "'opsz' 24" }}>Founder OS · Venture</h3>
                <p className="text-[12px] text-white/50 mt-0.5">Stage · MVP · Cofounder Rahul K. + 2</p>
              </div>
              <div className="flex items-center gap-1.5">
                {["List", "Kanban", "Timeline"].map((v, i) => (
                  <button key={v} className={`text-[11px] px-2.5 py-1 rounded-md font-grotesk ${i === 1 ? "bg-white/10 text-white border border-white/15" : "text-white/50 hover:text-white/80"}`}>{v}</button>
                ))}
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {cols.map((col) => (
                <div key={col.title} className="rounded-xl border border-white/[0.06] bg-white/[0.015] p-3">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="w-1.5 h-1.5 rounded-full" style={{ background: col.accent }} />
                      <span className="text-[11px] uppercase tracking-widest text-white/60 font-grotesk font-semibold">{col.title}</span>
                    </div>
                    <span className="text-[10px] text-white/30 font-grotesk">{col.items.length}</span>
                  </div>
                  <div className="flex flex-col gap-2">
                    {col.items.map((it, i) => (
                      <div key={i} className="rounded-lg bg-white/[0.04] border border-white/[0.05] p-2.5 hover:border-white/15 transition-colors">
                        <div className="flex items-start gap-2">
                          {it.check ? (
                            <Check size={12} className="text-emerald-400 mt-0.5 flex-shrink-0" />
                          ) : (
                            <Circle size={12} className="text-white/30 mt-0.5 flex-shrink-0" />
                          )}
                          <span className={`text-[12px] leading-snug ${it.check ? "text-white/40 line-through" : "text-white/85"}`}>{it.t}</span>
                        </div>
                        <div className="mt-2 flex items-center gap-1.5">
                          <span className="text-[9px] uppercase tracking-widest font-grotesk px-1.5 py-0.5 rounded" style={{ background: col.accent + "22", color: col.accent }}>{it.tag}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </ScreenFrame>
  );
};

/* ------------------------------------------------------------------ */
/* 3. Learn / LMS screen                                              */
/* ------------------------------------------------------------------ */

export const LearnScreen = () => (
  <ScreenFrame title="app.bowerschool.com — /learning">
    <div className="flex min-h-[520px]">
      <Sidebar activeLabel="Learning" />
      <div className="flex-1 min-w-0">
        <TopBar />
        <div className="p-5 md:p-6">
          <div className="mb-4">
            <span className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Year 2 · Semester 3</span>
            <h3 className="font-display text-white text-xl md:text-2xl font-semibold tracking-tight mt-0.5" style={{ fontVariationSettings: "'opsz' 24" }}>Growth Marketing &amp; Distribution</h3>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-[1.5fr_1fr] gap-3">
            {/* Featured lesson card */}
            <div className="relative overflow-hidden rounded-xl border border-white/[0.06] p-5" style={{ background: "linear-gradient(135deg, rgba(66,66,255,0.18) 0%, rgba(139,92,246,0.1) 100%)" }}>
              <div className="text-[10px] uppercase tracking-widest text-white/60 font-grotesk mb-1.5">Module 4 · Video · 18 min</div>
              <h4 className="font-display text-white text-xl font-semibold tracking-tight mb-2" style={{ fontVariationSettings: "'opsz' 22" }}>Building your first growth loop</h4>
              <p className="text-[12px] text-white/55 max-w-sm mb-4">Learn how to design flywheels, referral loops, and content loops that compound &mdash; with case studies from Notion, Superhuman, and Airtable.</p>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#F06292] flex items-center justify-center">
                  <Play size={12} className="text-white ml-0.5 fill-white" />
                </div>
                <div>
                  <div className="text-[11px] text-white/80">Ajit Rangnekar</div>
                  <div className="text-[10px] text-white/40">Ex-Dean, ISB · Practitioner</div>
                </div>
                <button className="ml-auto text-[11px] px-3 py-1.5 rounded-full bg-white text-black font-semibold font-grotesk">Resume</button>
              </div>
              {/* Progress bar */}
              <div className="mt-4">
                <div className="flex items-center justify-between text-[10px] text-white/50 mb-1 font-grotesk"><span>Progress</span><span>68%</span></div>
                <div className="h-1 rounded-full bg-white/10 overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#4242FF]" style={{ width: "68%" }} />
                </div>
              </div>
            </div>
            {/* Journal */}
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] uppercase tracking-widest text-white/50 font-grotesk">Weekly Journal</span>
                <span className="text-[10px] text-emerald-400 font-grotesk">Streak · 6w</span>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-white/40 font-grotesk mb-1">Wed 22 Jul</div>
              <p className="text-[13px] text-white/80 leading-relaxed">Users are pulled in by the AI Copilot but bounce before onboarding. Hypothesis: <span className="text-white">the onboarding is too long. Test with 3 users tomorrow.</span></p>
              <div className="mt-3 flex flex-wrap gap-1.5">
                {["#growth", "#activation", "#insight"].map((t) => (
                  <span key={t} className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] text-white/60 font-grotesk">{t}</span>
                ))}
              </div>
            </div>
          </div>
          {/* Module list */}
          <div className="mt-4 rounded-xl border border-white/[0.06] bg-white/[0.02]">
            {[
              { t: "01 · The activation flywheel", done: true },
              { t: "02 · Referral loops that compound", done: true },
              { t: "03 · Paid vs organic — the calculus", done: true },
              { t: "04 · Building your first growth loop", done: false, active: true },
              { t: "05 · Distribution channels & fit", done: false },
            ].map((r, i, arr) => (
              <div key={i} className={`flex items-center gap-3 px-4 py-3 ${i < arr.length - 1 ? "border-b border-white/[0.05]" : ""} ${r.active ? "bg-white/[0.03]" : ""}`}>
                {r.done ? (
                  <div className="w-5 h-5 rounded-full bg-emerald-500/25 border border-emerald-500/40 flex items-center justify-center"><Check size={11} className="text-emerald-300" /></div>
                ) : r.active ? (
                  <div className="w-5 h-5 rounded-full border border-[#8B5CF6] flex items-center justify-center bg-[#8B5CF6]/20"><Play size={9} className="text-[#B794F6] ml-0.5" /></div>
                ) : (
                  <div className="w-5 h-5 rounded-full border border-white/15" />
                )}
                <span className={`text-[13px] font-grotesk ${r.done ? "text-white/50 line-through" : r.active ? "text-white" : "text-white/70"}`}>{r.t}</span>
                <span className="ml-auto text-[10px] text-white/30 font-grotesk">{r.done ? "Complete" : r.active ? "Now" : "Locked"}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </ScreenFrame>
);

/* ------------------------------------------------------------------ */
/* 4. Community screen                                                */
/* ------------------------------------------------------------------ */

const AVATAR_GRAD = [
  "linear-gradient(135deg, #FF9F43, #F06292)",
  "linear-gradient(135deg, #4242FF, #A855F7)",
  "linear-gradient(135deg, #10B981, #06B6D4)",
  "linear-gradient(135deg, #F59E0B, #EF4444)",
  "linear-gradient(135deg, #8B5CF6, #EC4899)",
];
const Avatar = ({ i = 0, size = 32, letter = "R" }) => (
  <div className="rounded-full text-white font-grotesk font-semibold flex items-center justify-center flex-shrink-0" style={{ width: size, height: size, fontSize: size * 0.42, background: AVATAR_GRAD[i % AVATAR_GRAD.length] }}>{letter}</div>
);

export const ConnectScreen = () => (
  <ScreenFrame title="app.bowerschool.com — /community">
    <div className="flex min-h-[520px]">
      <Sidebar activeLabel="Community" />
      <div className="flex-1 min-w-0">
        <TopBar />
        <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-[1.6fr_1fr] gap-3">
          {/* Feed */}
          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Avatar i={0} letter="R" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white">Rahul K. <span className="text-white/40">· 2m</span></div>
                  <div className="text-[10px] text-white/40 font-grotesk">Founder · Founder OS · Year 2</div>
                </div>
                <MoreHorizontal size={14} className="text-white/40" />
              </div>
              <p className="text-[13px] text-white/85 leading-relaxed">Just crossed <span className="text-white">10 paying users</span> for Founder OS 🎉 Turns out the referral loop we shipped last week actually works. Anyone here scaled a referral loop past 100 users? Would love a chat.</p>
              <div className="mt-3 flex items-center gap-4 text-[11px] font-grotesk text-white/50">
                <span className="flex items-center gap-1"><Flame size={12} className="text-orange-400" /> 42</span>
                <span className="flex items-center gap-1"><MessageSquare size={12} /> 14</span>
                <span className="flex items-center gap-1"><ArrowUpRight size={12} /> Share</span>
              </div>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center gap-2 mb-3">
                <Avatar i={2} letter="A" />
                <div className="flex-1 min-w-0">
                  <div className="text-[13px] text-white">Aryan V. <span className="text-white/40">· 12m</span></div>
                  <div className="text-[10px] text-white/40 font-grotesk">Founder · PropEase · Year 3</div>
                </div>
              </div>
              <p className="text-[13px] text-white/85 leading-relaxed">Looking for a <span className="text-white">frontend co-founder</span> to join PropEase. Traction: 240 properties, $12k MRR. DM if you&rsquo;re equally deranged about property tech.</p>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#8B5CF6]/25 text-[#B794F6] font-grotesk">Cofounder wanted</span>
                <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-white/[0.06] text-white/60 font-grotesk">Frontend</span>
              </div>
            </div>
          </div>
          {/* Right rail */}
          <div className="flex flex-col gap-3">
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] uppercase tracking-widest text-white/50 font-grotesk">Mentor Slots · This week</span>
              </div>
              <div className="flex flex-col gap-2">
                {[
                  { name: "Ajit R.", role: "Ex-Dean, ISB", when: "Wed · 10:30", i: 1 },
                  { name: "MSR", role: "Ex-CEO, T-Hub", when: "Fri · 15:00", i: 2 },
                  { name: "Asha L.", role: "Ex-ISB Registrar", when: "Sat · 11:00", i: 4 },
                ].map((m, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Avatar i={m.i} letter={m.name[0]} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-white truncate">{m.name}</div>
                      <div className="text-[10px] text-white/40 truncate font-grotesk">{m.role}</div>
                    </div>
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-white/[0.06] text-white/60 font-grotesk">{m.when}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="text-[11px] uppercase tracking-widest text-white/50 font-grotesk mb-3">Cofounder Match · 3 new</div>
              <div className="flex flex-col gap-2.5">
                {[
                  { name: "Kirti V.", skill: "Design", match: 92 },
                  { name: "Aaryan V.", skill: "Backend", match: 88 },
                  { name: "Krittik V.", skill: "AI/ML", match: 84 },
                ].map((c, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <Avatar i={i + 1} letter={c.name[0]} />
                    <div className="flex-1 min-w-0">
                      <div className="text-[12px] text-white">{c.name}</div>
                      <div className="text-[10px] text-white/40 font-grotesk">{c.skill}</div>
                    </div>
                    <div className="text-[11px] font-grotesk font-semibold text-emerald-400">{c.match}%</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ScreenFrame>
);

/* ------------------------------------------------------------------ */
/* 5. AI Copilot screen                                               */
/* ------------------------------------------------------------------ */

const CHAT = [
  { role: "user", text: "Plan my day. I have an MVP due tonight and a mentor 1:1 with Ajit." },
  { role: "ai", text: "Got it Rahul. Here&rsquo;s your optimized plan:", plan: [
    { time: "09:00–10:15", t: "Ship MVP onboarding fix (from yesterday&rsquo;s journal)", tag: "Deep work" },
    { time: "10:30–11:00", t: "Mentor 1:1 with Ajit — prep note attached", tag: "Mentor" },
    { time: "11:15–13:00", t: "Founder OS launch checklist · finalize copy", tag: "Ship" },
    { time: "14:00–15:30", t: "5 user interviews (2 booked)", tag: "Discovery" },
    { time: "18:00", t: "MVP submission · 30 min buffer", tag: "Deadline" },
  ]},
];

export const AICopilotScreen = () => (
  <ScreenFrame title="app.bowerschool.com — /copilot">
    <div className="flex min-h-[520px]">
      <Sidebar activeLabel="AI Copilot" />
      <div className="flex-1 min-w-0 flex flex-col">
        <TopBar />
        <div className="flex-1 min-w-0 flex flex-col md:flex-row">
          <div className="flex-1 min-w-0 p-5 md:p-6 flex flex-col gap-4">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-[#F06292] to-[#FF9F43] flex items-center justify-center">
                <Sparkles size={14} className="text-white" />
              </div>
              <div>
                <h3 className="text-white text-[15px] font-semibold tracking-tight">Founder Copilot</h3>
                <div className="text-[10px] text-white/40 font-grotesk">GPT · trained on your OS · always awake</div>
              </div>
            </div>
            {CHAT.map((m, i) => (
              m.role === "user" ? (
                <div key={i} className="self-end max-w-[420px] rounded-2xl rounded-br-md bg-gradient-to-br from-[#4242FF] to-[#7C3AED] px-4 py-2.5 text-[13px] text-white leading-relaxed">{m.text}</div>
              ) : (
                <div key={i} className="max-w-[520px]">
                  <div className="rounded-2xl rounded-bl-md bg-white/[0.05] border border-white/[0.08] px-4 py-2.5 text-[13px] text-white/90 leading-relaxed" dangerouslySetInnerHTML={{ __html: m.text }} />
                  {m.plan && (
                    <div className="mt-2 rounded-2xl border border-white/[0.08] bg-white/[0.02] overflow-hidden">
                      {m.plan.map((p, j, arr) => (
                        <div key={j} className={`flex items-center gap-3 px-4 py-2.5 ${j < arr.length - 1 ? "border-b border-white/[0.05]" : ""}`}>
                          <span className="text-[10px] font-grotesk text-white/50 w-24">{p.time}</span>
                          <div className="w-1 h-6 rounded-full bg-gradient-to-b from-[#8B5CF6] to-[#4242FF]" />
                          <span className="flex-1 text-[12px] text-white/85 truncate">{p.t}</span>
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60 font-grotesk">{p.tag}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )
            ))}
            <TypingIndicator />
            {/* Input */}
            <div className="mt-auto pt-2">
              <div className="flex items-center gap-2 px-3 py-2.5 rounded-2xl border border-white/[0.1] bg-white/[0.04]">
                <Paperclip size={14} className="text-white/40" />
                <span className="text-[13px] text-white/40 flex-1">Ask anything about your OS…</span>
                <button className="w-7 h-7 rounded-full bg-gradient-to-br from-[#4242FF] to-[#7C3AED] flex items-center justify-center">
                  <Send size={12} className="text-white" />
                </button>
              </div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {["Review my deck", "Draft a mentor update", "Find 5 investors like Kunal Shah", "Summarize this cohort"].map((s) => (
                  <span key={s} className="text-[10px] px-2 py-1 rounded-full bg-white/[0.03] border border-white/[0.06] text-white/60 font-grotesk">{s}</span>
                ))}
              </div>
            </div>
          </div>
          {/* Right context rail */}
          <div className="hidden md:flex w-[260px] flex-shrink-0 border-l border-white/[0.06] p-4 flex-col gap-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Context</div>
            <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50 font-grotesk mb-1"><Rocket size={11} /> Venture</div>
              <div className="text-[12px] text-white">Founder OS · MVP · 48%</div>
            </div>
            <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50 font-grotesk mb-1"><Trophy size={11} /> Founder Score</div>
              <div className="text-[12px] text-white">842 <span className="text-emerald-400 text-[10px]">+24</span></div>
            </div>
            <div className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-3">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-widest text-white/50 font-grotesk mb-1"><PenLine size={11} /> Latest journal</div>
              <div className="text-[12px] text-white/85 leading-snug">&ldquo;Onboarding too long, bouncing…&rdquo;</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ScreenFrame>
);

const TypingIndicator = () => (
  <div className="flex items-center gap-1.5 px-4">
    <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "0ms" }} />
    <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "120ms" }} />
    <span className="w-1.5 h-1.5 rounded-full bg-white/40 animate-bounce" style={{ animationDelay: "240ms" }} />
    <span className="text-[10px] text-white/40 font-grotesk ml-1">Copilot is thinking</span>
  </div>
);

/* ------------------------------------------------------------------ */
/* 6. Raise / Investor screen                                         */
/* ------------------------------------------------------------------ */

export const RaiseScreen = () => (
  <ScreenFrame title="app.bowerschool.com — /investors">
    <div className="flex min-h-[520px]">
      <Sidebar activeLabel="Investor Connect" />
      <div className="flex-1 min-w-0">
        <TopBar />
        <div className="p-5 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-display text-white text-xl md:text-2xl font-semibold tracking-tight" style={{ fontVariationSettings: "'opsz' 24" }}>Investor Room</h3>
              <p className="text-[12px] text-white/50 mt-0.5">Founder OS · Seed · $500K raising · 4 committed</p>
            </div>
            <button className="text-[11px] px-3 py-1.5 rounded-full bg-white text-black font-semibold font-grotesk inline-flex items-center gap-1.5">Send update <ArrowRight size={11} /></button>
          </div>
          {/* Pipeline */}
          <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] overflow-hidden">
            <div className="grid grid-cols-6 items-center px-4 py-2.5 border-b border-white/[0.05] text-[10px] uppercase tracking-widest text-white/40 font-grotesk">
              <span className="col-span-2">Investor</span>
              <span>Check</span>
              <span>Stage</span>
              <span>Signal</span>
              <span className="text-right">Next</span>
            </div>
            {[
              { name: "Kunal Shah", firm: "Angel", check: "$50k", stage: "Committed", signal: 95, next: "Wire · Fri", i: 0 },
              { name: "Blume Ventures", firm: "Institutional · Seed", check: "$250k", stage: "Term sheet", signal: 82, next: "Call · Mon", i: 1 },
              { name: "Sequoia Surge", firm: "Program", check: "$100k", stage: "DD", signal: 68, next: "Follow-up", i: 4 },
              { name: "Y Combinator", firm: "Accelerator", check: "$125k", stage: "Applied", signal: 40, next: "Interview · Aug 3", i: 2 },
              { name: "Antler", firm: "Program", check: "$50k", stage: "Passed", signal: 12, next: "—", i: 3 },
            ].map((r, i, arr) => (
              <div key={i} className={`grid grid-cols-6 items-center px-4 py-3 ${i < arr.length - 1 ? "border-b border-white/[0.05]" : ""} hover:bg-white/[0.02] transition-colors`}>
                <div className="col-span-2 flex items-center gap-2.5">
                  <Avatar i={r.i} size={26} letter={r.name[0]} />
                  <div className="min-w-0">
                    <div className="text-[13px] text-white truncate">{r.name}</div>
                    <div className="text-[10px] text-white/40 truncate font-grotesk">{r.firm}</div>
                  </div>
                </div>
                <span className="text-[13px] text-white/85 font-grotesk font-semibold">{r.check}</span>
                <span className={`text-[10px] uppercase tracking-widest font-grotesk font-semibold ${r.stage === "Committed" ? "text-emerald-400" : r.stage === "Passed" ? "text-white/30" : "text-[#B794F6]"}`}>{r.stage}</span>
                <div className="flex items-center gap-2">
                  <div className="w-12 h-1 rounded-full bg-white/[0.08] overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#4242FF]" style={{ width: `${r.signal}%` }} />
                  </div>
                  <span className="text-[10px] text-white/50 font-grotesk">{r.signal}</span>
                </div>
                <span className="text-[11px] text-white/60 text-right font-grotesk">{r.next}</span>
              </div>
            ))}
          </div>
          {/* Bottom KPIs */}
          <div className="mt-4 flex gap-3">
            <KPI icon={DollarSign} label="Committed" value="$300K" delta="60%" dark />
            <KPI icon={Users} label="In pipeline" value="12" />
            <KPI icon={Clock} label="Avg cycle" value="18d" delta="↓ 4d" />
            <KPI icon={ArrowUpRight} label="Meetings/wk" value="6" delta="+2" />
          </div>
        </div>
      </div>
    </div>
  </ScreenFrame>
);

/* ------------------------------------------------------------------ */
/* 7. Scale / Marketplace                                             */
/* ------------------------------------------------------------------ */

export const ScaleScreen = () => (
  <ScreenFrame title="app.bowerschool.com — /marketplace">
    <div className="flex min-h-[520px]">
      <Sidebar activeLabel="Growth" />
      <div className="flex-1 min-w-0">
        <TopBar />
        <div className="p-5 md:p-6">
          <div className="mb-4">
            <span className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Marketplace · Internships · Alumni</span>
            <h3 className="font-display text-white text-xl md:text-2xl font-semibold tracking-tight mt-0.5" style={{ fontVariationSettings: "'opsz' 24" }}>Grow past the seed.</h3>
          </div>
          {/* Filter chips */}
          <div className="flex flex-wrap gap-1.5 mb-4">
            {["All 84", "Founding Eng", "Design", "Growth", "Ops", "Internship 22", "Full-time 62"].map((c, i) => (
              <span key={c} className={`text-[11px] px-2.5 py-1 rounded-full font-grotesk ${i === 0 ? "bg-white text-black font-semibold" : "bg-white/[0.04] border border-white/10 text-white/60"}`}>{c}</span>
            ))}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[
              { title: "Founding Engineer", venture: "Founder OS", stage: "Seed · Series A", loc: "Hyderabad · Hybrid", tag: "Full-time", equity: "0.4–1.2%", i: 0 },
              { title: "Growth Intern", venture: "PropEase", stage: "Bootstrapped · $12k MRR", loc: "Remote", tag: "Internship · 3mo", equity: "Stipend", i: 1 },
              { title: "Design Lead", venture: "ShortList", stage: "Pre-seed", loc: "Bengaluru", tag: "Full-time", equity: "1.5–3%", i: 2 },
              { title: "AI Engineer", venture: "Tycho Space", stage: "Angel round", loc: "Hyderabad", tag: "Full-time", equity: "0.6–1.5%", i: 3 },
              { title: "Community Lead", venture: "Bower Alumni", stage: "In-house", loc: "Hyderabad", tag: "Full-time", equity: "—", i: 4 },
              { title: "Fullstack Intern", venture: "Founder OS", stage: "Seed", loc: "Remote", tag: "Internship · 6mo", equity: "Stipend", i: 0 },
            ].map((c, i) => (
              <div key={i} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4 hover:border-white/15 transition-colors group">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Avatar i={c.i} size={30} letter={c.venture[0]} />
                    <div>
                      <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">{c.venture}</div>
                      <h4 className="text-[13px] text-white font-medium">{c.title}</h4>
                    </div>
                  </div>
                  <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded bg-white/[0.06] text-white/60 font-grotesk">{c.tag}</span>
                </div>
                <div className="flex flex-col gap-1.5 text-[11px] text-white/55 font-grotesk">
                  <span className="flex items-center gap-1.5"><Building2 size={11} /> {c.stage}</span>
                  <span className="flex items-center gap-1.5"><MapPin size={11} /> {c.loc}</span>
                  <span className="flex items-center gap-1.5"><Award size={11} /> {c.equity}</span>
                </div>
                <div className="mt-3 flex items-center justify-between">
                  <span className="text-[10px] text-white/40 font-grotesk">Posted 2d ago</span>
                  <span className="text-[11px] text-[#B794F6] group-hover:text-white transition-colors inline-flex items-center gap-0.5">Apply <ArrowRight size={11} className="group-hover:translate-x-1 transition-transform" /></span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  </ScreenFrame>
);

/* ------------------------------------------------------------------ */
/* 8. Founder Score / Intelligence screen                             */
/* ------------------------------------------------------------------ */

const RadialGauge = ({ value = 842, max = 1000, size = 210, stroke = 14 }) => {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const t = setTimeout(() => setProgress(value / max), 250);
    return () => clearTimeout(t);
  }, [value, max]);
  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <defs>
          <linearGradient id="gauge-grad" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#A855F7" />
            <stop offset="50%" stopColor="#4242FF" />
            <stop offset="100%" stopColor="#06B6D4" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={r} stroke="rgba(255,255,255,0.06)" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2} cy={size / 2} r={r}
          stroke="url(#gauge-grad)" strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - progress)}
          style={{ transition: "stroke-dashoffset 1.8s cubic-bezier(0.16,1,0.3,1)" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-[10px] uppercase tracking-widest text-white/50 font-grotesk">Founder Score</span>
        <span className="font-display text-white text-[52px] font-semibold leading-none tracking-tight mt-1" style={{ fontVariationSettings: "'opsz' 96" }}>{value}</span>
        <span className="text-[11px] text-emerald-400 font-grotesk mt-1">↑ 24 this week</span>
      </div>
    </div>
  );
};

export const FounderScoreScreen = () => (
  <ScreenFrame title="app.bowerschool.com — /founder-score">
    <div className="flex min-h-[520px]">
      <Sidebar activeLabel="Founder Score" />
      <div className="flex-1 min-w-0">
        <TopBar />
        <div className="p-5 md:p-6 grid grid-cols-1 md:grid-cols-[auto_1fr] gap-6 items-start">
          <div className="flex justify-center md:justify-start">
            <div className="relative">
              <div className="absolute -inset-6 rounded-full opacity-40 blur-2xl" style={{ background: "radial-gradient(circle, #8B5CF6, transparent 70%)" }} />
              <RadialGauge />
            </div>
          </div>
          <div className="flex flex-col gap-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
              {[
                { label: "Build", value: 92, color: "#8B5CF6" },
                { label: "Learn", value: 88, color: "#4242FF" },
                { label: "Connect", value: 74, color: "#06B6D4" },
                { label: "Raise", value: 66, color: "#10B981" },
              ].map((d) => (
                <div key={d.label} className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-3">
                  <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk mb-1.5">{d.label}</div>
                  <div className="flex items-baseline gap-1.5">
                    <span className="text-xl font-semibold text-white tracking-tight">{d.value}</span>
                    <span className="text-[10px] text-white/40">/100</span>
                  </div>
                  <div className="mt-2 h-1 rounded-full bg-white/[0.06] overflow-hidden">
                    <div className="h-full rounded-full" style={{ background: d.color, width: `${d.value}%` }} />
                  </div>
                </div>
              ))}
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-[11px] uppercase tracking-widest text-white/50 font-grotesk">12-week trend</span>
                <div className="flex items-center gap-1">
                  {["3m", "6m", "1y"].map((v, i) => (
                    <span key={v} className={`text-[10px] px-1.5 py-0.5 rounded font-grotesk ${i === 0 ? "bg-white/10 text-white" : "text-white/40"}`}>{v}</span>
                  ))}
                </div>
              </div>
              <MiniSparkline values={[420, 450, 490, 520, 560, 610, 680, 720, 760, 790, 820, 842]} />
              <div className="flex justify-between text-[10px] text-white/40 font-grotesk mt-2"><span>W1</span><span>W6</span><span>W12</span></div>
            </div>
            <div className="rounded-xl border border-white/[0.06] bg-white/[0.02] p-4">
              <div className="text-[11px] uppercase tracking-widest text-white/50 font-grotesk mb-2 flex items-center gap-1.5"><Target size={11} /> Signals this week</div>
              <div className="flex flex-col gap-1.5">
                {[
                  { t: "Shipped 3 MVP features · +18", ok: true },
                  { t: "6 mentor hours logged · +9", ok: true },
                  { t: "Journal streak 6 wks · +5", ok: true },
                  { t: "Investor meetings behind cadence · –8", ok: false },
                ].map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px] font-grotesk">
                    <span className={`w-1.5 h-1.5 rounded-full ${s.ok ? "bg-emerald-400" : "bg-orange-400"}`} />
                    <span className={s.ok ? "text-white/85" : "text-white/60"}>{s.t}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </ScreenFrame>
);

/* ------------------------------------------------------------------ */
/* 9. Small "surface" screens for Intelligence bento & Meet-hero      */
/* ------------------------------------------------------------------ */

export const CollegeDashboardMini = () => (
  <div className="p-4">
    <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">College Dashboard</div>
    <div className="mt-1 text-white text-lg font-semibold">GITAM · Cohort C</div>
    <div className="mt-4 grid grid-cols-3 gap-2">
      {[{ v: "342", l: "Students" }, { v: "48", l: "Ventures" }, { v: "18", l: "Mentors" }].map((k, i) => (
        <div key={i} className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-2">
          <div className="text-white text-lg font-semibold">{k.v}</div>
          <div className="text-[10px] text-white/40 font-grotesk">{k.l}</div>
        </div>
      ))}
    </div>
    <div className="mt-3">
      <MiniSparkline values={[10, 20, 32, 40, 55, 70, 90, 110, 130, 150, 170]} color="#06B6D4" />
    </div>
  </div>
);

export const ParentMiniCard = () => (
  <div className="p-4">
    <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Parent Dashboard</div>
    <div className="mt-1 text-white text-lg font-semibold">Rahul K. · Year 2</div>
    <div className="mt-3 flex items-center gap-3">
      <div className="w-14 h-14 rounded-full bg-gradient-to-br from-[#4242FF] to-[#7C3AED] flex items-center justify-center">
        <span className="text-white text-xl font-semibold">842</span>
      </div>
      <div className="flex-1">
        <div className="text-[11px] text-white/70">Founder Score · <span className="text-emerald-400">↑ 24</span></div>
        <div className="mt-2 h-1 rounded-full bg-white/[0.06] overflow-hidden"><div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#4242FF]" style={{ width: "84%" }} /></div>
      </div>
    </div>
    <div className="mt-3 flex flex-col gap-1.5">
      {["Learning · on track", "Attendance · 96%", "Mentor sessions · 14h/mo"].map((r) => (
        <div key={r} className="flex items-center gap-2 text-[11px] font-grotesk"><Check size={11} className="text-emerald-400" /><span className="text-white/70">{r}</span></div>
      ))}
    </div>
  </div>
);

export const MentorMiniCard = () => (
  <div className="p-4">
    <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Mentor Dashboard</div>
    <div className="mt-1 text-white text-lg font-semibold">Ajit Rangnekar</div>
    <div className="mt-3 flex flex-col gap-2">
      {[
        { name: "Rahul K.", venture: "Founder OS", score: 842, i: 0 },
        { name: "Kirti V.", venture: "ShortList", score: 720, i: 1 },
        { name: "Aaryan V.", venture: "PropEase", score: 690, i: 2 },
      ].map((m, i) => (
        <div key={i} className="flex items-center gap-2">
          <Avatar i={m.i} size={26} letter={m.name[0]} />
          <div className="flex-1 min-w-0">
            <div className="text-[12px] text-white truncate">{m.name}</div>
            <div className="text-[10px] text-white/40 truncate font-grotesk">{m.venture}</div>
          </div>
          <span className="text-[11px] font-grotesk font-semibold text-[#B794F6]">{m.score}</span>
        </div>
      ))}
    </div>
  </div>
);

export const InvestorMiniCard = () => (
  <div className="p-4">
    <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Investor Dashboard</div>
    <div className="mt-1 text-white text-lg font-semibold">Bower Capital · Fund I</div>
    <div className="mt-3 grid grid-cols-2 gap-2">
      {[{ v: "$12M", l: "AUM" }, { v: "18", l: "Portfolio" }, { v: "2.4x", l: "TVPI" }, { v: "6", l: "Exits" }].map((k, i) => (
        <div key={i} className="rounded-lg bg-white/[0.03] border border-white/[0.06] p-2">
          <div className="text-white text-base font-semibold">{k.v}</div>
          <div className="text-[10px] text-white/40 font-grotesk">{k.l}</div>
        </div>
      ))}
    </div>
    <div className="mt-3">
      <MiniSparkline values={[100, 110, 120, 130, 145, 165, 180, 200, 220, 240, 260]} color="#10B981" />
    </div>
  </div>
);

/* ------------------------------------------------------------------ */
/* 10. Mobile mini widgets — for MobileExperience section             */
/* ------------------------------------------------------------------ */

export const MobileWidget = ({ variant, className = "" }) => {
  const base = "w-full h-full bg-[#0b0b12] flex flex-col";
  switch (variant) {
    case "dashboard":
      return (
        <div className={`${base} ${className}`}>
          <div className="px-4 pt-6 pb-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Wed · 22 Jul</div>
            <div className="mt-1 text-white text-2xl font-display font-semibold tracking-tight" style={{ fontVariationSettings: "'opsz' 24" }}>Good morning, <span className="bg-clip-text text-transparent" style={{ backgroundImage: "linear-gradient(135deg, #B794F6, #4242FF)" }}>Rahul.</span></div>
          </div>
          <div className="px-4">
            <div className="rounded-2xl border border-white/[0.08] p-3" style={{ background: "linear-gradient(135deg, rgba(139,92,246,0.2), rgba(66,66,255,0.08))" }}>
              <div className="text-[10px] uppercase tracking-widest text-white/60 font-grotesk">Founder Score</div>
              <div className="text-white text-3xl font-display font-semibold tracking-tight mt-0.5" style={{ fontVariationSettings: "'opsz' 32" }}>842</div>
              <div className="text-[10px] text-emerald-400 font-grotesk">↑ 24 this week</div>
            </div>
          </div>
          <div className="flex-1 px-4 pt-3 flex flex-col gap-2 overflow-hidden">
            {[
              { time: "10:30", t: "Mentor · Ajit R.", c: "#8B5CF6" },
              { time: "13:00", t: "Sprint · Cohort C", c: "#4242FF" },
              { time: "18:00", t: "MVP submission", c: "#F06292" },
            ].map((r, i) => (
              <div key={i} className="flex items-center gap-2.5 rounded-xl bg-white/[0.03] border border-white/[0.05] p-2.5">
                <div className="w-1 h-8 rounded-full" style={{ background: r.c }} />
                <div className="flex-1">
                  <div className="text-[11px] text-white/85">{r.t}</div>
                  <div className="text-[9px] text-white/40 font-grotesk">{r.time}</div>
                </div>
              </div>
            ))}
          </div>
          <div className="border-t border-white/[0.06] px-4 py-3 flex justify-around">
            {[Home, Rocket, Sparkles, MessageSquare, Users].map((I, i) => (
              <I key={i} size={16} className={i === 0 ? "text-white" : "text-white/40"} />
            ))}
          </div>
        </div>
      );
    case "copilot":
      return (
        <div className={`${base} ${className}`}>
          <div className="px-4 pt-6 pb-3 flex items-center gap-2 border-b border-white/[0.05]">
            <div className="w-7 h-7 rounded-md bg-gradient-to-br from-[#F06292] to-[#FF9F43] flex items-center justify-center"><Sparkles size={13} className="text-white" /></div>
            <div>
              <div className="text-[13px] text-white font-semibold">Copilot</div>
              <div className="text-[9px] text-white/40 font-grotesk">Always awake</div>
            </div>
          </div>
          <div className="flex-1 p-3 flex flex-col gap-2 overflow-hidden">
            <div className="self-end max-w-[85%] rounded-2xl rounded-br-md bg-gradient-to-br from-[#4242FF] to-[#7C3AED] px-3 py-2 text-[11px] text-white">Plan today</div>
            <div className="max-w-[90%] rounded-2xl rounded-bl-md bg-white/[0.05] border border-white/[0.08] px-3 py-2 text-[11px] text-white/85">Ship MVP fix → Mentor 1:1 → 5 interviews → Submit at 6pm.</div>
            <div className="max-w-[75%] rounded-xl bg-white/[0.03] border border-white/[0.06] p-2">
              <div className="text-[9px] uppercase tracking-widest text-white/40 font-grotesk">Founder Score</div>
              <div className="text-white text-lg font-semibold">842 <span className="text-[9px] text-emerald-400">↑ 24</span></div>
            </div>
          </div>
          <div className="px-3 pb-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-full border border-white/[0.1] bg-white/[0.04]">
              <span className="text-[11px] text-white/40 flex-1">Ask Copilot…</span>
              <div className="w-6 h-6 rounded-full bg-gradient-to-br from-[#4242FF] to-[#7C3AED] flex items-center justify-center"><Send size={10} className="text-white" /></div>
            </div>
          </div>
        </div>
      );
    case "score":
      return (
        <div className={`${base} ${className} items-center justify-center p-4`}>
          <RadialGauge value={842} size={170} stroke={12} />
          <div className="mt-6 w-full grid grid-cols-2 gap-2">
            {[
              { l: "Build", v: 92, c: "#8B5CF6" }, { l: "Learn", v: 88, c: "#4242FF" },
              { l: "Connect", v: 74, c: "#06B6D4" }, { l: "Raise", v: 66, c: "#10B981" },
            ].map((d) => (
              <div key={d.l} className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-2">
                <div className="text-[9px] uppercase tracking-widest text-white/40 font-grotesk">{d.l}</div>
                <div className="text-white text-sm font-semibold">{d.v}</div>
                <div className="mt-1 h-0.5 rounded-full bg-white/[0.06] overflow-hidden"><div className="h-full rounded-full" style={{ background: d.c, width: `${d.v}%` }} /></div>
              </div>
            ))}
          </div>
        </div>
      );
    case "community":
      return (
        <div className={`${base} ${className}`}>
          <div className="px-4 pt-6 pb-3 border-b border-white/[0.06]">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Community</div>
            <div className="text-white text-lg font-semibold">Feed</div>
          </div>
          <div className="flex-1 p-3 flex flex-col gap-2 overflow-hidden">
            {[
              { name: "Rahul K.", txt: "10 paying users 🎉", i: 0 },
              { name: "Kirti V.", txt: "Looking for design partner…", i: 1 },
              { name: "Aaryan V.", txt: "PropEase hit $12k MRR", i: 2 },
            ].map((p, i) => (
              <div key={i} className="rounded-xl bg-white/[0.03] border border-white/[0.05] p-2.5">
                <div className="flex items-center gap-2 mb-1"><Avatar i={p.i} size={20} letter={p.name[0]} /><span className="text-[11px] text-white">{p.name}</span></div>
                <div className="text-[11px] text-white/70">{p.txt}</div>
                <div className="flex items-center gap-3 text-[10px] text-white/40 mt-1"><span className="flex items-center gap-0.5"><Flame size={9} className="text-orange-400" />24</span><span className="flex items-center gap-0.5"><MessageSquare size={9} />6</span></div>
              </div>
            ))}
          </div>
        </div>
      );
    case "learn":
      return (
        <div className={`${base} ${className}`}>
          <div className="px-4 pt-6 pb-3">
            <div className="text-[10px] uppercase tracking-widest text-white/40 font-grotesk">Learning</div>
            <div className="text-white text-lg font-semibold">Year 2 · Growth</div>
          </div>
          <div className="px-3 pb-3">
            <div className="relative overflow-hidden rounded-2xl p-3" style={{ background: "linear-gradient(135deg, rgba(66,66,255,0.2), rgba(139,92,246,0.1))" }}>
              <div className="text-[9px] uppercase tracking-widest text-white/60 font-grotesk mb-1">Now playing</div>
              <div className="text-white text-sm font-semibold">Building your first growth loop</div>
              <div className="mt-3 flex items-center gap-2">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-[#FF6B6B] to-[#F06292] flex items-center justify-center"><Play size={10} className="text-white ml-0.5 fill-white" /></div>
                <div className="flex-1 h-1 rounded-full bg-white/10 overflow-hidden"><div className="h-full bg-gradient-to-r from-[#8B5CF6] to-[#4242FF]" style={{ width: "68%" }} /></div>
                <span className="text-[9px] text-white/60">18m</span>
              </div>
            </div>
          </div>
          <div className="flex-1 px-3 flex flex-col gap-1.5 overflow-hidden">
            {["01 Activation flywheel", "02 Referral loops", "03 Paid vs organic", "04 Growth loop", "05 Distribution fit"].map((r, i) => (
              <div key={r} className="flex items-center gap-2 rounded-lg bg-white/[0.02] border border-white/[0.05] px-2.5 py-1.5">
                {i < 3 ? <Check size={10} className="text-emerald-400" /> : i === 3 ? <Play size={10} className="text-[#B794F6] ml-0 fill-[#B794F6]" /> : <Circle size={10} className="text-white/25" />}
                <span className={`text-[11px] ${i < 3 ? "text-white/40" : i === 3 ? "text-white" : "text-white/60"}`}>{r}</span>
              </div>
            ))}
          </div>
        </div>
      );
    default:
      return null;
  }
};

/* ------------------------------------------------------------------ */
/* Chrome-less "device screen" wrapper (for phone-frames)             */
/* ------------------------------------------------------------------ */
export const DeviceScreen = ({ variant, className = "" }) => (
  <div className={`w-full h-full ${className}`}><MobileWidget variant={variant} /></div>
);
